import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ShieldAlert, Users, MonitorSmartphone, XCircle, CheckCircle2, Loader2, LogOut, Globe,
} from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const ACTION_LABELS = {
  login_success: { label: "Accesso riuscito", ok: true },
  login_failed: { label: "Password errata", ok: false },
  login_blocked: { label: "Accesso bloccato (account disattivo)", ok: false },
  login_locked: { label: "Account bloccato (brute force)", ok: false },
  login_password_ok: { label: "Password OK (attesa 2FA)", ok: true },
  twofa_failed: { label: "Codice 2FA errato", ok: false },
  logout: { label: "Logout", ok: true },
  user_created: { label: "Utente creato", ok: true },
  user_updated: { label: "Utente modificato", ok: true },
  user_deleted: { label: "Utente eliminato", ok: true },
  session_revoked: { label: "Sessione revocata", ok: true },
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`w-5 h-5 ${accent}`} strokeWidth={1.75} />
      </div>
      <p className="font-heading text-3xl font-black text-slate-900 mt-3" data-testid={`sec-stat-${label}`}>{value}</p>
    </div>
  );
}

export default function Security() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.get("/admin/security/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/admin/sessions").then((r) => setSessions(r.data)).catch(() => {}),
      api.get("/admin/audit-logs?limit=150").then((r) => setLogs(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const revoke = async (sid) => {
    try {
      await api.delete(`/admin/sessions/${sid}`);
      toast.success("Sessione revocata");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-blue-600" /> Sicurezza
        </h1>
        <p className="text-slate-500 mt-1">Monitoraggio accessi, sessioni attive e log di audit.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard icon={Users} label="Utenti attivi" value={stats ? `${stats.active_users}/${stats.total_users}` : "—"} accent="text-blue-600" />
        <StatCard icon={MonitorSmartphone} label="Sessioni attive" value={stats?.active_sessions ?? "—"} accent="text-emerald-600" />
        <StatCard icon={CheckCircle2} label="Accessi 24h" value={stats?.successful_logins_24h ?? "—"} accent="text-emerald-600" />
        <StatCard icon={XCircle} label="Falliti 24h" value={stats?.failed_logins_24h ?? "—"} accent="text-red-500" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-8" data-testid="sessions-panel">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">Sessioni attive</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Nessuna sessione attiva.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sessions.map((s) => (
              <li key={s.sid} className="flex items-center justify-between px-5 py-4" data-testid={`session-${s.sid}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{s.name} <span className="text-slate-400 font-normal">· {s.email}</span></p>
                  <p className="text-sm text-slate-500 flex items-center gap-1.5 truncate">
                    <Globe className="w-3.5 h-3.5" /> {s.ip} · attiva {fmtTime(s.last_seen)}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => revoke(s.sid)} data-testid={`revoke-${s.sid}`}
                  className="text-slate-500 hover:text-red-600 shrink-0">
                  <LogOut className="w-4 h-4 mr-1.5" /> Revoca
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="audit-panel">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">Log di audit</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Nessun evento registrato.</div>
        ) : (
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left px-5 py-3 font-bold">Evento</th>
                  <th className="text-left px-5 py-3 font-bold">Utente</th>
                  <th className="text-left px-5 py-3 font-bold">IP</th>
                  <th className="text-left px-5 py-3 font-bold">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((l) => {
                  const meta = ACTION_LABELS[l.action] || { label: l.action, ok: l.success };
                  return (
                    <tr key={l.id} className="hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                      <td className="px-5 py-3">
                        <Badge variant="secondary"
                          className={meta.ok ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                          {meta.label}
                        </Badge>
                        {l.detail && <p className="text-xs text-slate-400 mt-1">{l.detail}</p>}
                      </td>
                      <td className="px-5 py-3 text-slate-600">{l.email || "—"}</td>
                      <td className="px-5 py-3 text-slate-500 font-mono text-xs">{l.ip || "—"}</td>
                      <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtTime(l.timestamp)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
